/**
 * O que faz: aba "Agentes" da página de IA, na ordem de uso: o que está esperando revisão (topo), os dois
 *            agentes (liga/desliga, IA e prazos), a organização dos contatos (assistente guiado, 21/10/2026) e o
 *            mapa marco → coluna enquanto o motor legado estiver ativo. Admin vê o painel técnico da organização.
 *            Os resultados ficam na aba Métricas.
 * Onde usa: ConfiguracoesIAPage (rota /ia), para planos PMK e Premium (o banco também confere o plano).
 * Conexões: useAgentes (configuração, rotas, revisão, ações), etapas da clínica, coluna de retorno e regras Meta.
 * Tabelas: indiretamente lead_classification_settings/routes, lead_return_suggestions e lead_classification_state.
 * Data: 25/09/2026 (reorganizada no design system Gens em 26/09/2026).
 */
import { useEffect, useMemo, useState } from 'react';
import { Bot, CalendarClock, Loader2, Play, Save, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useEtapas } from '@/hooks/useEtapasData';
import { useReturnKanbanSettings } from '@/features/returns/useReturnKanbanSettings';
import { useMetaConversionEventRules } from '@/hooks/useMetaConversionEventRules';
import { CONFIGURACAO_PADRAO, type ConfiguracaoAgentes, type ModoRota } from '../contratos';
import { useAcoesAgentes, useConfiguracaoAgentes } from '../useAgentes';
import { ConfiguracaoOrganizacaoKanban } from '../organizacao/ConfiguracaoOrganizacaoKanban';
import { LimitesOrganizacaoAdmin } from '../organizacao/LimitesOrganizacaoAdmin';
import type { EtapaOrganizacao } from '../organizacao/rascunho';
import { useOrganizacaoKanban } from '../organizacao/useOrganizacaoKanban';
import { CardParaRevisar } from './CardParaRevisar';
import { MapaMarcosColunas, type EtapaPipeline } from './MapaMarcosColunas';

interface SecaoAgentesProps {
  clinicaId: string;
  modoAdmin: boolean;
  // Pedido vindo da URL (sino de avisos) para abrir o modal de revisão assim que a aba carregar.
  abrirRevisao?: boolean;
  onAbriuRevisao?: () => void;
}

// Autorização separada e explícita: mover sozinho para essas colunas envia eventos para os anúncios.
function AutorizacaoColunasMeta({ liberada, desabilitada, onAlterar }: { liberada: boolean; desabilitada: boolean; onAlterar: (valor: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-3">
      <div className="space-y-1">
        <Label htmlFor="agentes-libera-meta">Permitir mover sozinho para colunas de venda ou com evento Meta</Label>
        <p className="text-xs text-muted-foreground">
          Ao mover para essas colunas, o CRM envia eventos como QualifiedLead e Purchase para os anúncios.
          Com isto ligado, um erro da IA também vira evento enviado. A coluna de retorno continua só sugestão.
        </p>
      </div>
      <Switch id="agentes-libera-meta" checked={liberada} disabled={desabilitada} onCheckedChange={onAlterar} />
    </div>
  );
}

function inteiroEntre(valor: string, minimo: number, maximo: number): number | null {
  const numero = Number(valor);
  return Number.isInteger(numero) && numero >= minimo && numero <= maximo ? numero : null;
}

export function SecaoAgentes({ clinicaId, modoAdmin, abrirRevisao = false, onAbriuRevisao }: SecaoAgentesProps) {
  const consulta = useConfiguracaoAgentes(clinicaId);
  const acoes = useAcoesAgentes(clinicaId, modoAdmin);
  const { data: etapas = [] } = useEtapas(clinicaId);
  const retorno = useReturnKanbanSettings({ enabled: Boolean(clinicaId) });
  const regrasMeta = useMetaConversionEventRules(clinicaId);
  const organizacao = useOrganizacaoKanban(clinicaId);
  // Assistente controlado aqui: o cartão e o "Ajustar regra da coluna" da revisão abrem o mesmo rascunho.
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  // Trocar de clínica fecha o assistente; o rascunho da clínica anterior nunca aparece na nova.
  useEffect(() => { setAssistenteAberto(false); }, [clinicaId]);

  // Rascunho local: a clínica ajusta tudo e salva uma vez; nada vai ao banco a cada clique de switch.
  const [rascunho, setRascunho] = useState<ConfiguracaoAgentes>(CONFIGURACAO_PADRAO);
  const [prazos, setPrazos] = useState({ inatividade: '7', espera: '30', retorno: '30' });
  useEffect(() => {
    const atual = consulta.data?.configuracao;
    if (!atual) return;
    setRascunho(atual);
    setPrazos({ inatividade: String(atual.inactiveAfterDays), espera: String(atual.limiteEsperaMinutos), retorno: String(atual.prazoRetornoPadraoDias) });
  }, [consulta.data?.configuracao]);

  const colunasMetaProtegidas = useMemo(() => new Set(
    (regrasMeta.rulesQuery.data ?? [])
      .filter((regra) => regra.is_active && regra.trigger_type === 'kanban_stage' && regra.etapa_kanban_id)
      .map((regra) => regra.etapa_kanban_id as string),
  ), [regrasMeta.rulesQuery.data]);

  // Colunas protegidas (venda, retorno ou evento Meta) não viram origem padrão nem entram marcadas no assistente.
  const colunaRetornoId = retorno.data?.etapa_kanban_id ?? null;
  const etapasOrganizacao = useMemo<EtapaOrganizacao[]>(() => (etapas as EtapaPipeline[]).map((etapa, indice) => ({
    id: etapa.id,
    nome: etapa.nome,
    ordem: etapa.ordem ?? indice,
    protegida: Boolean(etapa.eh_etapa_vendas) || etapa.id === colunaRetornoId || colunasMetaProtegidas.has(etapa.id),
  })), [etapas, colunaRetornoId, colunasMetaProtegidas]);
  // Motor personalizado publicado substitui o mapa de marcos: as duas configurações não disputam as mesmas rotas.
  const organizacaoAtiva = organizacao.consulta.data?.motor === 'hybrid' && (organizacao.consulta.data?.regras.length ?? 0) > 0;

  const inatividade = inteiroEntre(prazos.inatividade, 1, 90);
  const espera = inteiroEntre(prazos.espera, 5, 1440);
  const prazoRetorno = inteiroEntre(prazos.retorno, 1, 180);
  // Validação de tela (o servidor valida de novo): valores fora da faixa bloqueiam o salvar com aviso claro.
  const prazosValidos = inatividade !== null && espera !== null && prazoRetorno !== null;
  const retornoSemIA = rascunho.agenteRetornoAtivo && !rascunho.iaHabilitada;

  const salvar = () => {
    if (!prazosValidos || retornoSemIA) return;
    acoes.salvarConfiguracao.mutate({
      ...rascunho,
      inactiveAfterDays: inatividade as number,
      limiteEsperaMinutos: espera as number,
      prazoRetornoPadraoDias: prazoRetorno as number,
    });
  };

  if (consulta.isLoading) {
    return <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando agentes…</div>;
  }
  if (consulta.isError) {
    return (
      <Card><CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-muted-foreground">
        Não foi possível carregar a configuração dos agentes.
        <Button size="sm" variant="outline" onClick={() => consulta.refetch()}>Tentar novamente</Button>
      </CardContent></Card>
    );
  }

  const algumAgenteLigado = consulta.data?.configuracao.enabled === true;
  const rotas = consulta.data?.rotas ?? [];
  const liberaColunasMeta = consulta.data?.configuracao.iaPodeMoverColunasMeta === true;

  return (
    // Carregando → conteúdo com fade, sem troca seca.
    <div className="space-y-6 animate-in fade-in-0 duration-300 motion-reduce:animate-none">
      {/* 1. O que pede ação vem primeiro: a revisão é o uso diário; a configuração é pontual. */}
      <CardParaRevisar clinicaId={clinicaId} modoAdmin={modoAdmin} abrirAoCarregar={abrirRevisao} onAbriuAoCarregar={onAbriuRevisao}
        onAjustarOrganizacao={() => setAssistenteAberto(true)} />

      {/* 2. Configuração dos dois agentes, salva de uma vez pela barra abaixo dos cards. */}
      <section className="space-y-3" aria-labelledby="titulo-config-agentes">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="lg-rotulo">Configuração</p>
            <h3 id="titulo-config-agentes" className="text-base font-semibold text-foreground">Agentes</h3>
          </div>
          <span className={`${algumAgenteLigado ? 'lg-selo lg-selo--sucesso' : 'lg-selo lg-selo--neutro'} transition-colors duration-300`}>
            {algumAgenteLigado ? 'Agentes ativos' : 'Agentes desligados'}
          </span>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
        <Card className="lg-card">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base"><Workflow className="h-4 w-4" aria-hidden="true" />Classificador de conversas</CardTitle>
              <Switch
                aria-label="Ligar Classificador de conversas"
                checked={rascunho.agenteConversasAtivo}
                onCheckedChange={(valor) => setRascunho((atual) => ({ ...atual, agenteConversasAtivo: valor }))}
              />
            </div>
            <CardDescription>Lê cada conversa, identifica em que ponto do funil o lead está e sugere ou move o card para a coluna que você escolher.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="agentes-ia" className="text-sm font-medium">Ler as conversas com IA</Label>
                <p className="text-xs text-muted-foreground">Sem isso, o agente usa só os registros do CRM (agenda, vendas). Custo em Custos de IA.</p>
              </div>
              <Switch id="agentes-ia" checked={rascunho.iaHabilitada} onCheckedChange={(valor) => setRascunho((atual) => ({ ...atual, iaHabilitada: valor }))} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="agentes-inatividade">Sem contato recente após (dias)</Label>
                <Input id="agentes-inatividade" inputMode="numeric" value={prazos.inatividade} aria-invalid={inatividade === null}
                  onChange={(evento) => setPrazos((atual) => ({ ...atual, inatividade: evento.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="agentes-espera">Avisar quando o lead espera há (minutos)</Label>
                <Input id="agentes-espera" inputMode="numeric" value={prazos.espera} aria-invalid={espera === null}
                  onChange={(evento) => setPrazos((atual) => ({ ...atual, espera: evento.target.value }))} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg-card">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base"><CalendarClock className="h-4 w-4" aria-hidden="true" />Classificador de retornos</CardTitle>
              <Switch
                aria-label="Ligar Classificador de retornos"
                checked={rascunho.agenteRetornoAtivo}
                onCheckedChange={(valor) => setRascunho((atual) => ({ ...atual, agenteRetornoAtivo: valor }))}
              />
            </div>
            <CardDescription>Encontra pedidos como “volto mês que vem” e sugere o retorno na agenda, já com data.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="agentes-retorno-modo">Quando encontrar um pedido de retorno</Label>
              <Select value={rascunho.agenteRetornoModo} onValueChange={(valor) => setRascunho((atual) => ({ ...atual, agenteRetornoModo: valor as ModoRota }))}>
                <SelectTrigger id="agentes-retorno-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="suggestion">Sugerir para a equipe aprovar</SelectItem>
                  <SelectItem value="automatic">Agendar o retorno sozinho</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="agentes-prazo-retorno">Prazo quando o lead não diz a data (dias)</Label>
              <Input id="agentes-prazo-retorno" inputMode="numeric" value={prazos.retorno} aria-invalid={prazoRetorno === null}
                onChange={(evento) => setPrazos((atual) => ({ ...atual, retorno: evento.target.value }))} />
            </div>
            {retornoSemIA && <p className="text-xs text-warning-text animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none">Este agente precisa da leitura das conversas com IA ligada.</p>}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button className="rounded-full" onClick={salvar} disabled={!prazosValidos || retornoSemIA || acoes.salvarConfiguracao.isPending}>
          {acoes.salvarConfiguracao.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="mr-2 h-4 w-4" aria-hidden="true" />}
          Salvar agentes
        </Button>
        <Button variant="outline" className="rounded-full" onClick={() => acoes.analisarAgora.mutate()} disabled={!algumAgenteLigado || acoes.analisarAgora.isPending}>
          {acoes.analisarAgora.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="mr-2 h-4 w-4" aria-hidden="true" />}
          Analisar agora
        </Button>
        {!prazosValidos && <p role="alert" className="text-xs lg-texto-perigo animate-in fade-in-0 slide-in-from-left-1 duration-200 motion-reduce:animate-none">Revise os prazos: dias de 1 a 90, espera de 5 a 1440 minutos e retorno de 1 a 180 dias.</p>}
      </div>
      </section>

      {/* 3. Organização dos contatos: significado de cada coluna, configurado pelo assistente guiado. */}
      <ConfiguracaoOrganizacaoKanban
        key={clinicaId}
        clinicaId={clinicaId}
        etapas={etapasOrganizacao}
        assistenteAberto={assistenteAberto}
        onAssistenteAbertoChange={setAssistenteAberto}
      />

      {/* 4. Enquanto o motor legado está ativo, o mapa marco → coluna continua sendo quem decide o destino. */}
      {!organizacaoAtiva ? (
        <Card className="lg-card animate-in fade-in-0 duration-300 motion-reduce:animate-none">
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base"><Bot className="h-4 w-4" aria-hidden="true" />Marcos do funil e colunas</CardTitle>
            <CardDescription>
              O agente nunca lê o nome das suas colunas: ele identifica o marco e você diz para qual coluna cada marco leva.
              A IA só avança o card no funil (exceto Perdido) e só move sozinha com confiança alta.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <MapaMarcosColunas
              etapas={etapas as EtapaPipeline[]}
              rotas={rotas}
              colunaRetornoId={retorno.data?.etapa_kanban_id ?? null}
              colunasMetaProtegidas={colunasMetaProtegidas}
              liberaColunasMeta={liberaColunasMeta}
              desabilitado={acoes.salvarRota.isPending || acoes.removerRota.isPending}
              onSalvar={(rota) => acoes.salvarRota.mutate(rota)}
              onRemover={(categoria) => acoes.removerRota.mutate(categoria)}
            />
            <AutorizacaoColunasMeta liberada={liberaColunasMeta} desabilitada={!algumAgenteLigado || acoes.liberarColunasMeta.isPending}
              onAlterar={(valor) => acoes.liberarColunasMeta.mutate(valor)} />
          </CardContent>
        </Card>
      ) : (
        // Com a organização ativa, a autorização Meta continua valendo para os movimentos automáticos calibrados.
        <Card className="lg-card animate-in fade-in-0 duration-300 motion-reduce:animate-none">
          <CardContent className="p-4">
            <AutorizacaoColunasMeta liberada={liberaColunasMeta} desabilitada={!algumAgenteLigado || acoes.liberarColunasMeta.isPending}
              onAlterar={(valor) => acoes.liberarColunasMeta.mutate(valor)} />
          </CardContent>
        </Card>
      )}

      {/* 5. Painel técnico: só quando um admin abre a aba de uma clínica. */}
      {modoAdmin && <LimitesOrganizacaoAdmin key={clinicaId} clinicaId={clinicaId} />}

    </div>
  );
}
